'use client';
import React, { useState } from 'react';
import ProgressBar from '@/components/ProgressBar';
import { MapPin, ChevronDown, ChevronUp } from 'lucide-react';

const routes = [
  { id: 'dr-001', technician: 'Torres, J.', zone: 'Irvine / Newport', stops: 9, completed: 7, miles: 41, revenue: '$7,820', efficiency: 96, status: 'On Call 7', eta: '4:45 PM' },
  { id: 'dr-002', technician: 'Reyes, M.', zone: 'Costa Mesa / CM', stops: 8, completed: 6, miles: 38, revenue: '$6,940', efficiency: 92, status: 'En Route', eta: '5:10 PM' },
  { id: 'dr-003', technician: 'Park, D.', zone: 'Santa Ana / Tustin', stops: 7, completed: 7, miles: 29, revenue: '$5,460', efficiency: 89, status: 'Complete', eta: 'Done' },
  { id: 'dr-004', technician: 'Patel, A.', zone: 'Anaheim / Fullerton', stops: 8, completed: 5, miles: 44, revenue: '$7,200', efficiency: 84, status: 'On Call 5', eta: '5:30 PM' },
  { id: 'dr-005', technician: 'Chen, L.', zone: 'Lake Forest / MV', stops: 6, completed: 6, miles: 22, revenue: '$4,880', efficiency: 91, status: 'Complete', eta: 'Done' },
  { id: 'dr-006', technician: 'Williams, K.', zone: 'Buena Park / Brea', stops: 9, completed: 4, miles: 51, revenue: '$8,100', efficiency: 79, status: 'On Call 4', eta: '6:00 PM' },
  { id: 'dr-007', technician: 'Santos, R.', zone: 'Laguna / Dana Pt', stops: 7, completed: 5, miles: 33, revenue: '$5,900', efficiency: 88, status: 'On Call 5', eta: '5:20 PM' },
  { id: 'dr-008', technician: 'Kim, D.', zone: 'Garden Grove / Westminster', stops: 8, completed: 3, miles: 47, revenue: '$6,200', efficiency: 76, status: 'On Call 3', eta: '6:30 PM' },
];

type SortKey = 'efficiency' | 'revenue' | 'stops';
type SortDir = 'asc' | 'desc';

export default function DispatcherCopilotTable() {
  const [sortKey, setSortKey] = useState<SortKey>('efficiency');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sorted = [...routes].sort((a, b) => {
    let av: number, bv: number;
    if (sortKey === 'revenue') {
      av = parseFloat(a.revenue.replace(/[$,]/g, ''));
      bv = parseFloat(b.revenue.replace(/[$,]/g, ''));
    } else if (sortKey === 'efficiency') {
      av = a.efficiency; bv = b.efficiency;
    } else {
      av = a.stops; bv = b.stops;
    }
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortIcon = ({ k }: { k: SortKey }) =>
    sortKey === k
      ? (sortDir === 'desc' ? <ChevronDown size={12} className="text-secondary" /> : <ChevronUp size={12} className="text-secondary" />)
      : <ChevronDown size={12} className="text-muted-foreground/40" />;

  return (
    <div className="bg-card rounded-lg card-shadow border border-border">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Dispatcher Copilot — Live Routes</h3>
          <p className="text-xs text-muted-foreground mt-0.5">91 routes optimized today · 4.7 hrs drive time recovered</p>
        </div>
        <div className="flex items-center gap-1.5 bg-success/10 border border-success/20 rounded-md px-3 py-1.5">
          <MapPin size={12} className="text-success" />
          <span className="text-xs font-medium text-success">$184 fuel saved</span>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs min-w-max">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Technician</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Zone</th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground cursor-pointer select-none" onClick={() => toggleSort('stops')}>
                <div className="flex items-center justify-end gap-1">Stops <SortIcon k="stops" /></div>
              </th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Progress</th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Miles</th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground cursor-pointer select-none" onClick={() => toggleSort('revenue')}>
                <div className="flex items-center justify-end gap-1">Revenue <SortIcon k="revenue" /></div>
              </th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground cursor-pointer select-none" onClick={() => toggleSort('efficiency')}>
                <div className="flex items-center justify-end gap-1">Eff% <SortIcon k="efficiency" /></div>
              </th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Status</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">ETA Done</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5 font-medium text-foreground whitespace-nowrap">{r.technician}</td>
                <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{r.zone}</td>
                <td className="px-3 py-2.5 text-right font-tabular text-foreground">{r.stops}</td>
                <td className="px-3 py-2.5 w-28">
                  <ProgressBar value={r.completed} max={r.stops} color="success" size="sm" />
                  <span className="text-muted-foreground text-xs">{r.completed}/{r.stops}</span>
                </td>
                <td className="px-3 py-2.5 text-right font-tabular text-muted-foreground">{r.miles}</td>
                <td className="px-3 py-2.5 text-right font-semibold font-tabular text-foreground">{r.revenue}</td>
                <td className="px-3 py-2.5 text-right">
                  <span className={`font-semibold font-tabular text-xs ${r.efficiency >= 90 ? 'text-success' : r.efficiency >= 80 ? 'text-warning' : 'text-danger'}`}>{r.efficiency}%</span>
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <span className={`text-xs font-medium ${r.status === 'Complete' ? 'text-success' : r.status === 'En Route' ? 'text-secondary' : 'text-foreground'}`}>{r.status}</span>
                </td>
                <td className="px-3 py-2.5 text-muted-foreground font-tabular whitespace-nowrap">{r.eta}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-3 border-t border-border grid grid-cols-4 gap-4 text-xs">
        <div><span className="text-muted-foreground">Total Stops:</span> <span className="font-semibold font-tabular text-foreground ml-1">62</span></div>
        <div><span className="text-muted-foreground">Completed:</span> <span className="font-semibold font-tabular text-success ml-1">43</span></div>
        <div><span className="text-muted-foreground">Total Revenue:</span> <span className="font-semibold font-tabular text-foreground ml-1">$52,500</span></div>
        <div><span className="text-muted-foreground">Avg Efficiency:</span> <span className="font-semibold font-tabular text-foreground ml-1">87%</span></div>
      </div>
    </div>
  );
}